import React, { useState } from 'react';
import { RotateCcw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { RedispatchDialog } from './RedispatchDialog';

const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso || '-'; } };

const GroupCard = ({ g, onRedispatch }) => {
  const [sel, setSel] = useState(() => new Set(g.bundles.map((b) => b.bundle_barcode)));
  const toggle = (bc) => setSel((p) => { const n = new Set(p); if (n.has(bc)) n.delete(bc); else n.add(bc); return n; });
  return (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 space-y-3" data-testid={`otr-group-${g.original_entry_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold text-[#1A1F2E]">Doc {g.document_barcode || '-'}</div>
          <div className="text-sm text-[#5A5F6E]">
            {g.transfer_type === 'job_worker' ? 'Job Worker' : 'Internal Transfer'} → {g.destination || '-'} · Previous vehicle {g.original_vehicle_no || '-'}
          </div>
        </div>
        <Badge variant="destructive">{g.bundles.length} returned</Badge>
      </div>
      <div className="rounded-md border border-[#F0EBDF] divide-y divide-[#F0EBDF]">
        {g.bundles.map((b) => (
          <label key={b.bundle_barcode} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
            <Checkbox checked={sel.has(b.bundle_barcode)} onCheckedChange={() => toggle(b.bundle_barcode)} data-testid={`otr-select-${b.bundle_barcode}`} />
            <span className="font-mono font-medium">{b.bundle_barcode}</span>
            <span className="text-[#8A8F9E] ml-auto text-xs">{fmt(b.returned_at)}{b.return_reason ? ` · ${b.return_reason}` : ''}</span>
          </label>
        ))}
      </div>
      <Button size="sm" disabled={sel.size === 0} onClick={() => onRedispatch(g, [...sel])} className="bg-[#2D5F4F] hover:bg-[#244d40]" data-testid={`otr-redispatch-btn-${g.original_entry_id}`}>
        <RotateCcw className="w-4 h-4 mr-1" /> Re-dispatch ({sel.size})
      </Button>
    </div>
  );
};

export const RedispatchPanel = ({ groups, search, setSearch, staffId, geo, onChanged }) => {
  const [active, setActive] = useState(null);
  return (
    <div className="space-y-4" data-testid="otr-redispatch-panel">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F9E]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search bundle, document, destination, vehicle" className="pl-9" data-testid="otr-search-input" />
      </div>
      {groups.length === 0
        ? <p className="text-sm text-[#8A8F9E] bg-white rounded-lg border border-[#E5DFD0] p-6" data-testid="otr-no-pending">No returned bundles awaiting re-dispatch.</p>
        : groups.map((g) => <GroupCard key={`${g.original_entry_id}-${g.bundles.length}`} g={g} onRedispatch={(grp, codes) => setActive({ grp, codes })} />)}
      {active && (
        <RedispatchDialog group={active.grp} codes={active.codes} staffId={staffId} geo={geo}
          onClose={() => setActive(null)} onDone={() => { setActive(null); onChanged(); }} />
      )}
    </div>
  );
};

export default RedispatchPanel;
